import axios from 'axios';
import axiosInstance from './AxiosService';

const NetworkService = {
//   Job Schedule Services 

  fetchNetworkAuth: async () => {
    const response = await axiosInstance.get(`network-auth/`);
    return response.data;
  },

  createNetworkAuth: async (networkAuthData) => {
    const response = await axiosInstance.post(`network-auth/`, networkAuthData);
    return response.data;
  }, 

  updateNetworkAuth: async (networkID, networkAuthData) => {
    const response = await axiosInstance.put(`network-auth/${networkID}/`, networkAuthData);
    return response.data;
  },

  // New methods for multiple domain support
  fetchNetworkDomains: async () => {
    const response = await axiosInstance.get(`network-domains/`);
    return response.data;
  },

  createNetworkDomain: async (domainData) => {
    const response = await axiosInstance.post(`network-domains/`, domainData);
    return response.data;
  },

  updateNetworkDomain: async (domainId, domainData) => {
    const response = await axiosInstance.put(`network-domains/${domainId}/`, domainData);
    return response.data;
  },

  deleteNetworkDomain: async (domainId) => {
    const response = await axiosInstance.delete(`network-domains/${domainId}/`);
    return response.data;
  },

  // Methods for testing connection
  testDomainConnection: async (domainId, credentials) => {
    try {
      const response = await axiosInstance.post(`network-domains/${domainId}/test_connection/`, credentials);
      return response.data;
    } catch (error) {
      console.error('Error testing domain connection:', error);
      throw error;
    }
  },

  // Methods for job scheduling
  fetchNetworkJobs: async () => {
    try {
      const response = await axiosInstance.get('network-job-schedule/');
      return response.data;
    } catch (error) {
      console.error('Error fetching network jobs:', error);
      throw error;
    }
  },

  fetchNetworkJobById: async (jobId) => {
    try {
      const response = await axiosInstance.get(`network-job-schedule/${jobId}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching network job with ID ${jobId}:`, error);
      throw error;
    }
  },

  createNetworkJob: async (jobData) => {
    try {
      const response = await axiosInstance.post('network-job-schedule/', jobData);
      return response.data;
    } catch (error) {
      console.error('Error creating network job:', error);
      if (error.response && error.response.data) {
        throw new Error(error.response.data.message || 'Failed to create network job');
      }
      throw error;
    }
  },

  updateNetworkJob: async (jobId, jobData) => {
    try {
      const response = await axiosInstance.put(`network-job-schedule/${jobId}/`, jobData);
      return response.data;
    } catch (error) {
      console.error(`Error updating network job with ID ${jobId}:`, error);
      if (error.response && error.response.data) {
        throw new Error(error.response.data.message || 'Failed to update network job');
      }
      throw error;
    }
  },

  deleteNetworkJob: async (jobId) => {
    try {
      const response = await axiosInstance.delete(`network-job-schedule/${jobId}/`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting network job with ID ${jobId}:`, error);
      throw error;
    }
  },

  // Methods for terminated user analysis
  fetchTerminatedUserAnalysis: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.days) queryParams.append('days', params.days);
      if (params.domain) queryParams.append('domain', params.domain);
      
      const url = `network-analysis/terminated-users/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching terminated user analysis:', error);
      throw error;
    }
  },

  // Methods for job logs
  fetchNetworkJobLogs: async () => {
    try {
      const response = await axiosInstance.get('network-job-logs/');
      return response.data;
    } catch (error) {
      console.error('Error fetching network job logs:', error);
      throw error;
    }
  },

  // Method to run a job immediately
  runNetworkJob: async (jobId) => {
    try {
      const response = await axiosInstance.post(`network-job-schedule/${jobId}/run/`);
      
      // Check if we have results in the response
      if (response.data && response.data.results) {
        console.log('Got job results directly in run response:', response.data.results);
        return {
          ...response.data,
          // Extract the results data to make it accessible in the calling component
          resultsData: response.data.results
        };
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error running job with ID ${jobId}:`, error);
      throw error;
    }
  },

  // Method to get a specific job
  getNetworkJob: async (jobId) => {
    try {
      const response = await axiosInstance.get(`network-job-schedule/${jobId}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching job with ID ${jobId}:`, error);
      throw error;
    }
  },

  // Methods for job results analysis
  getJobResults: async (jobId) => {
    try {
      console.log('Fetching job results for ID:', jobId);
      const response = await axiosInstance.get(`job-results/${jobId}/`);
      console.log('Job results response:', response.data);
      
      // Check if the response has any data
      if (response.data && Array.isArray(response.data) && response.data.length === 0) {
        console.log('No results found for this job');
        return [];
      }
      
      // Check if we got a job_result_id instead of actual results
      if (response.data && response.data.job_result_id) {
        console.log('Got a job_result_id, fetching actual results:', response.data.job_result_id);
        const resultsResponse = await axiosInstance.get(`job-results/${response.data.job_result_id}/`);
        return resultsResponse.data;
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching results for job ${jobId}:`, error);
      throw error;
    }
  },

  getLatestJobResults: async (jobType) => {
    try {
      const response = await axiosInstance.get(`job-results/latest/${jobType}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching latest results for job type ${jobType}:`, error);
      throw error;
    }
  },

  // Admin groups analysis
  getAdminGroupAnalysis: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const url = `network-analysis/admin-groups/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching admin group analysis:', error);
      throw error;
    }
  },

  // Terminated users analysis
  getTerminatedUserAnalysis: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const url = `network-analysis/terminated-users/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching terminated user analysis:', error);
      throw error;
    }
  },

  // Patch installation analysis
  getPatchAnalysis: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const url = `network-analysis/patches/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching patch analysis:', error);
      throw error;
    }
  },

  // Export results as CSV
  exportJobResults: async (jobId, format = 'csv') => {
    try {
      const response = await axiosInstance.get(`job-results/${jobId}/export/?format=${format}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error(`Error exporting results for job ${jobId}:`, error);
      throw error;
    }
  },

  // SOX Compliance Admin Users
  getSoxAdmins: async (domainId = '') => {
    try {
      const queryParams = new URLSearchParams();
      if (domainId) {
        queryParams.append('domain', domainId);
      }
      
      const url = `network-analysis/sox-admins/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching SOX admin users:', error);
      throw error;
    }
  }
};

export default NetworkService;
